import { PillCRA as Pill, PrioSpinner } from '@prio365/prio365-react-library';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import Flex from '../../../../../../components/Flex';
import { PrioTheme } from '../../../../../../theme/types';
import {
  EIncomingInvoiceState,
  IncomingInvoiceDto,
} from '../../../../../../models/IncomingInvoices';

interface IncomingInvoiceStatusPillProps {
  invoice: IncomingInvoiceDto;
  size?: 'small' | 'default' | 'large';
}

export const IncomingInvoiceStatusPill: React.FC<
  IncomingInvoiceStatusPillProps
> = (props) => {
  //#region ------------------------------ Defaults
  const { invoice, size } = props;
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  //#endregion

  if (invoice?.incomingInvoiceState === EIncomingInvoiceState.Processing) {
    return (
      <Pill
        type="neutral"
        size={size ?? 'small'}
        style={{ backgroundColor: theme.colors.application.background.light }}
      >
        <Flex.Row childrenGap={4} alignItems="center">
          <PrioSpinner size="small" />
          {t('accounting:incomingInvoices.processing')}
        </Flex.Row>
      </Pill>
    );
  }

  const isApprovedToday =
    invoice?.approvedAt &&
    new Date(invoice?.approvedAt).toDateString() === new Date().toDateString();

  switch (invoice?.incomingInvoiceState) {
    case EIncomingInvoiceState.Draft:
      return (
        <Pill
          type="neutral"
          size="small"
          iconProp={['fas', 'circle']}
          style={{
            color: theme.colors.base.blue.default,
            backgroundColor: theme.colors.base.blue[30],
          }}
        >
          {t('accounting:incomingInvoices.status.draft')}
        </Pill>
      );
    case EIncomingInvoiceState.ApprovalRequested:
      return (
        <Pill
          type="default"
          size="small"
          iconProp={['fas', 'circle']}
          style={{
            color: '#715902',
            backgroundColor: theme.colors.base.yellow[20],
          }}
        >
          {t('accounting:incomingInvoices.status.approvalRequested')}
        </Pill>
      );
    case EIncomingInvoiceState.Rejected:
      return (
        <Pill
          type="default"
          size="small"
          iconProp={['fas', 'circle']}
          style={{
            color: theme.colors.base.red.default,
            backgroundColor: theme.colors.base.red[20],
          }}
        >
          {t('accounting:incomingInvoices.status.rejected')}
        </Pill>
      );
    case EIncomingInvoiceState.Approved:
      return isApprovedToday ? (
        <Pill
          type="default"
          size="small"
          iconProp={['fas', 'circle']}
          style={{
            color: '#136743',
            backgroundColor: theme.colors.base.green[20],
          }}
        >
          {t('accounting:incomingInvoices.status.approved')}
        </Pill>
      ) : null;
    case EIncomingInvoiceState.MissingFile:
      return (
        <Pill
          type="default"
          size="small"
          iconProp={['fas', 'circle']}
          style={{
            color: '#715902',
            backgroundColor: theme.colors.base.yellow[20],
          }}
        >
          {t('accounting:incomingInvoices.status.missingReceipt')}
        </Pill>
      );
    default:
      return (
        <Pill type="neutral" size="small">
          Unbekannt
        </Pill>
      );
  }
};

export default IncomingInvoiceStatusPill;
